import { useParams } from '@tanstack/react-router';

import { Text } from '../../cdl/Text/Text';
import { AttachmentsInfoPopover } from '../../common/Attachments/components/AttachmentsInfoPopover';
import { Divider } from '../../common/Divider/Divider';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { HeaderWithLine } from '../../common/HeaderWithLine/HeaderWithLine';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { useFlags } from '../../common/launchDarkly/useFlags';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../common/OfferStatusTag/OfferStatusTag';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { ReadOnlyProductTable } from '../../common/ReadOnlyProductTable/ReadOnlyProductTable';
import { Box } from '../../common/ui/Box';
import { useCustomerSettings } from '../../company/customer/detail/useCustomerSettings';
import { useOfferApprovalSettings } from '../MultipleApprovers/hooks/useOfferApprovalSettings';
import { OfferApprovalActivity } from '../OfferApprovalActivity';
import { OfferActivityTimeline } from '../OfferChat/OfferActivityTimeline';
import { OfferChatSection } from '../OfferChat/OfferChatSection';
import { OfferDatasheet } from '../OfferCoreData/OfferDatasheet';
import { InvolvedParties } from './common/components/InvolvedParties';
import { OfferDetailAttachments } from './common/components/OfferDetailAttachments';
import { useOfferDetails } from './common/hooks/useOfferDetails';
import { OfferPositions } from './OfferPositions/OfferPositions';

import { LubesOfferDetailBreadcrumb } from './components/LubesOfferDetailBreadcrumb';
import { OfferDetailActionBar } from './OfferDetailActionBar';
import { OfferDetailStatusBar } from './OfferDetailStatusBar';
import { LubesSupplierOfferDetailHeaderAction } from './LubesSupplierOfferDetailHeaderAction';
import { useFee } from './useFee';
import { selectEntityNameForOffer } from './selectEntityNameForOffer';

const Title = ({ offer }) => {
    return (
        offer.offerNumber ??
        selectEntityNameForOffer(offer, {
            order: translate('offer.detail.header.headlineOrder'),
            enquiry: translate('offer.detail.header.headlineEnquiry'),
        })
    );
};

const getDocumentTitle = (offer) => {
    return selectEntityNameForOffer(offer, {
        order: translate('page.offerShowOrder', {
            portName: offer.port.name,
            vesselName: offer.vessel.name,
        }),
        enquiry: translate('page.offerShowEnquiry', {
            portName: offer.port.name,
            vesselName: offer.vessel.name,
        }),
    });
};

export const OfferDetailPage = () => {
    const { id } = useParams({ from: '/_app/offer/$id' });
    const role = useRole();
    const flags = useFlags();
    const offerDetailsQuery = useOfferDetails(id);
    const offerApprovalSettingsQuery = useOfferApprovalSettings(id, role.isCustomer());
    const feeQuery = useFee(id);

    const offer = offerDetailsQuery.data;
    const customerSettingsQuery = useCustomerSettings(offer?.customerId, !!offer?.customerId && !!role.isCustomer());

    const documentTitle = offer ? getDocumentTitle(offer) : null;
    useDocumentTitle(documentTitle);

    const showFeeBox = Boolean(
        offer &&
            !offer.isCanceled() &&
            offer.state !== 'ENQUIRY' &&
            offer.isSpot() &&
            (role.isSuperAdmin() || role.isSupplier())
    );

    if (offerDetailsQuery.isPending || offerApprovalSettingsQuery.isLoading || customerSettingsQuery.isLoading) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (offerDetailsQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const productAndAttachmentSections = (
        <>
            <Box display="flex" flexDirection="column" marginBottom={16}>
                <Box display="flex" paddingBottom={16} justifyContent="space-between" alignItems="center">
                    <Text variant="title" as="h2">
                        {translate('offer.detail.products')}
                    </Text>
                </Box>

                <ReadOnlyProductTable
                    items={offer.items}
                    surcharges={offer.surcharges}
                    sampleKits={offer.samplekits}
                    totals={{ volume: offer.volume, price: offer.ppl, total: offer.total }}
                    showPrices
                />
            </Box>

            <Box>
                <Box display="flex" flexDirection="column" paddingBottom={16} gap={16}>
                    <Box display="flex" alignItems="center" gap={4}>
                        <Text variant="title" as="h2">
                            {translate('offer.detail.attachments')}
                        </Text>
                        <AttachmentsInfoPopover />
                    </Box>
                    {role.isSuperAdmin() || offer.isCanceled() ? <Divider /> : null}
                </Box>
                <OfferDetailAttachments offer={offer} />
            </Box>
        </>
    );

    return (
        <Page>
            <PageGrid>
                <Box display="grid" gap={4}>
                    <LubesOfferDetailBreadcrumb />
                    <HeaderWithLine>
                        <Box display="flex" flexDirection="row" alignItems="center" gap={12}>
                            <Text as="h1" variant="headline">
                                <Title offer={offer} />
                            </Text>
                            <OfferStatusTag state={offer.state} variant={role.isCustomer() ? 'customer' : 'supplier'} />
                        </Box>

                        <Box display="flex" flexDirection="row" alignItems="center" gap={8}>
                            {role.isCustomer() || role.isSuperAdmin() ? (
                                <Box marginRight={40}>
                                    <OfferApprovalActivity
                                        offer={offer}
                                        offerApprovalSettings={offerApprovalSettingsQuery.data}
                                    />
                                </Box>
                            ) : null}

                            <OfferDetailActionBar
                                offer={offer}
                                statusAction={
                                    role.isSupplier() ? <LubesSupplierOfferDetailHeaderAction offer={offer} /> : null
                                }
                            />
                        </Box>
                    </HeaderWithLine>
                </Box>

                <Box display="flex" flexDirection="column" rowGap={48} marginTop={16}>
                    <OfferDetailStatusBar offer={offer} customerSettings={customerSettingsQuery.data} />

                    <Box display="grid" gridTemplateColumns="minmax(0, 75fr) minmax(0, 25fr)" gap={48}>
                        <Box display="flex" flexDirection="column" gap={flags.activityLog ? 0 : 48}>
                            <Box>
                                <Box
                                    display="flex"
                                    paddingBottom={16}
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Text variant="title" as="h2">
                                        {translate('offer.detail.details')}
                                    </Text>
                                </Box>

                                <Box display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={48}>
                                    <InvolvedParties offer={offer} layout="vertical" />
                                    <OfferDatasheet offer={offer} fee={showFeeBox ? feeQuery.data?.fee : null} />
                                </Box>
                            </Box>

                            {!flags.activityLog ? productAndAttachmentSections : null}
                        </Box>

                        <Box display="flex" flexDirection="column" gap={48}>
                            <OfferPositions offer={offer} />
                            {flags.activityLog ? null : <OfferChatSection offer={offer} />}
                        </Box>
                    </Box>

                    {flags.activityLog ? (
                        <Box display="flex" flexDirection="column" gap={48}>
                            {productAndAttachmentSections}

                            <Box>
                                <Box paddingBottom={16}>
                                    <Text variant="title" as="h2">
                                        {translate('offer.detail.activity')}
                                    </Text>
                                </Box>

                                <OfferActivityTimeline offer={offer} />
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            </PageGrid>
        </Page>
    );
};
